// src/components/InlineImageSpread.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

/**
 * InlineImageSpread
 * ใช้สำหรับอ่าน eBook ที่เป็น "รูปหลายหน้า" (เช่น .webp หรือ .jpg)
 */

const InlineImageSpread = forwardRef(function InlineImageSpread(
  {
    pages = [],
    className = "",
    viewportHeight = 600,
    onStateChange,
    initialFit = "fitW",
    stackDirection = "horizontal", // horizontal = หนังสือ, vertical = ใบลาน
    pageGap = 0,
    disableSwipeNav = false,
  },
  ref
) {
  const [numPages, setNumPages] = useState(pages?.length || 0);
  const [page, setPage] = useState(1);
  const [dual, setDual] = useState(true);
  const [baseW, setBaseW] = useState(0);
  const [baseH, setBaseH] = useState(0);
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState(initialFit);
  const [wrapW, setWrapW] = useState(0);
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });

  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    setNumPages(pages?.length || 0);
    setPage(1);
    setBaseW(0);
    setBaseH(0);
  }, [pages]);

  // --- Media query: ถ้าจอเล็กให้เป็น single page ---
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const upd = () => setDual(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  // --- Track wrapper width ---
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([entry]) =>
      setWrapW(entry.contentRect.width)
    );
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // --- Auto fit scale ---
  useLayoutEffect(() => {
    if (!baseW || !baseH) return;

    const verticalPadding = 40; // pt-4 pb-6 โดยประมาณ
    const availH = Math.max(100, viewportHeight - verticalPadding);

    if (fitMode === "fitW") {
      const oneWidth = dual
        ? (wrapW - (dual ? pageGap : 0)) / (dual ? 2 : 1)
        : wrapW;
      const s = oneWidth / baseW;
      setScale(Math.min(3, Math.max(0.1, s)));
    } else if (fitMode === "fitH") {
      const s = availH / baseH;
      setScale(Math.min(3, Math.max(0.1, s)));
    }
  }, [fitMode, wrapW, viewportHeight, baseW, baseH, dual, pageGap]);

  // --- เลือกหน้าที่จะแสดง ---
  const visiblePages = useMemo(() => {
    if (!numPages) return [];
    if (!dual) return [page];

    if (stackDirection === "vertical") {
      return [page, Math.min(page + 1, numPages)];
    }

    const right = page % 2 === 0 ? page : Math.min(page + 1, numPages);
    const left = Math.max(1, right - 1);
    return [left, right];
  }, [page, numPages, dual, stackDirection]);

  const isSinglePage = visiblePages.length <= 1;

  // --- ส่ง control ออกภายนอก ---
  useImperativeHandle(ref, () => ({
    prev: () => setPage((p) => Math.max(1, p - (dual ? 2 : 1))),
    next: () =>
      setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1))),
    goto: (v) =>
      setPage(() => Math.max(1, Math.min(v, numPages || v))),
    zoomIn: () => {
      setFitMode("manual");
      setScale((s) => Math.min(3, s + 0.15));
    },
    zoomOut: () => {
      setFitMode("manual");
      setScale((s) => Math.max(0.1, s - 0.15));
    },
    fitWidth: () => setFitMode("fitW"),
    fitHeight: () => setFitMode("fitH"),
    toggleDual: () => setDual((d) => !d),
  }));

  // --- onStateChange callback ---
  useEffect(() => {
    onStateChange?.({ page, numPages, dual, fitMode, scale });
  }, [page, numPages, dual, fitMode, scale, onStateChange]);

  // --- Scroll sync ---
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () =>
      setScrollPos({ x: el.scrollLeft, y: el.scrollTop });
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo(scrollPos.x, scrollPos.y);
  }, [scale, scrollPos.x, scrollPos.y]);

  // --- Gesture drag / ปัดเปลี่ยนหน้า ---
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const drag = { active: false, sx: 0, sy: 0, scrollX: 0, scrollY: 0 };

    const ts = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      drag.active = true;
      drag.sx = t.clientX;
      drag.sy = t.clientY;
      drag.scrollX = el.scrollLeft;
      drag.scrollY = el.scrollTop;
    };
    const tm = (e) => {
      if (!drag.active) return;
      const t = e.touches?.[0];
      if (!t) return;
      el.scrollLeft = drag.scrollX - (t.clientX - drag.sx);
      el.scrollTop = drag.scrollY - (t.clientY - drag.sy);
    };
    const te = (e) => {
      if (!drag.active) return;
      drag.active = false;
      if (disableSwipeNav) return;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - drag.sx;
      const dy = t.clientY - drag.sy;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0)
          setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1)));
        else
          setPage((p) => Math.max(1, p - (dual ? 2 : 1)));
      }
    };

    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: true });
    el.addEventListener("touchend", te, { passive: true });

    // mouse drag
    const md = (e) => {
      drag.active = true;
      drag.sx = e.clientX;
      drag.sy = e.clientY;
      drag.scrollX = el.scrollLeft;
      drag.scrollY = el.scrollTop;
      el.style.cursor = "grabbing";
    };
    const mm = (e) => {
      if (!drag.active) return;
      el.scrollLeft = drag.scrollX - (e.clientX - drag.sx);
      el.scrollTop = drag.scrollY - (e.clientY - drag.sy);
    };
    const mu = () => {
      drag.active = false;
      el.style.cursor = "default";
    };
    el.addEventListener("mousedown", md);
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);

    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
      el.removeEventListener("mousedown", md);
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
    };
  }, [dual, numPages, disableSwipeNav]);

  // --- คีย์บอร์ดเปลี่ยนหน้า ---
  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        setPage((p) => Math.max(1, p - (dual ? 2 : 1)));
      }
      if (["ArrowRight", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        setPage((p) => Math.min(numPages || p, p + (dual ? 2 : 1)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dual, numPages]);

  const rowColClass =
    dual && stackDirection === "vertical" ? "flex-col" : "flex-row";

  const handleImgLoad = (e) => {
    if (!baseW || !baseH) {
      const iw = e.target.naturalWidth;
      const ih = e.target.naturalHeight;
      setBaseW(iw);
      setBaseH(ih);
    }
  };

  const pageBoxStyle = baseW && baseH
    ? { width: baseW * scale, height: baseH * scale }
    : {};

  return (
    <div
      ref={wrapRef}
      className={`relative ${className}`}
      style={{ height: viewportHeight }}
    >
      <div
        ref={scrollerRef}
        className="w-full h-full overflow-auto overscroll-contain bg-[#f3f3f3]"
      >
        <div className="w-max h-max mx-auto pt-4 pb-6">
          <div
            className={`flex ${rowColClass} items-start ${
              isSinglePage ? "justify-center" : ""
            }`}
            style={{ gap: dual ? pageGap : 0 }}
          >
            {visiblePages.map((p) => {
              const src = pages[p - 1];
              if (!src) return null;
              return (
                <div
                  key={p}
                  className="inline-block leading-none"
                  style={pageBoxStyle}
                >
                  <img
                    src={src}
                    alt={`page-${p}`}
                    onLoad={handleImgLoad}
                    className="block"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

export default InlineImageSpread;
